import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { MatCheckboxChange } from '@angular/material/checkbox'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import {
  ApiService,
  Appointment,
  AppointmentDataService,
  AppointmentEstimateProduct,
  AppointmentEstimateSchema,
  AppointmentModel,
  AppService,
  ChargeDescriptionMasterData,
  ChargeGrouping,
  Clinic,
  ClinicService,
  ConfirmService,
  CrudService,
  Eligibility,
  Estimate,
  EstimateInputs,
  EstimateModel,
  EstimateOutput,
  Facility,
  FacilityService,
  Invoice,
  InvoiceData,
  InvoiceLineItem,
  InvoiceModel,
  isInsuranceRelevantToVisit,
  isPerVisitInsuranceSelectionEnabled,
  isPricerError,
  isValidCPT,
  Patient,
  PatientDataService,
  PatientInsuranceStatus,
  PatientInsuranceType,
  PermissionsService,
  PriceModel,
  Procedure,
  ProcedureCodeModifier,
  ProcedureData,
  Product,
  ProductModel,
  seedChargeModifiers,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import Fuse from 'fuse.js'
import { extend, filter, isArray, isNumber, round } from 'lodash'
import moment from 'moment'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  startWith, takeUntil,
  tap
} from 'rxjs/operators'

import {
  ChargesDialogComponent,
  ChargesDialogData,
} from '../charges-dialog'
import { ProgressService } from '../progress'
import { buildPricerTraceMessage } from '../pricer-trace-message'

@Component({
  selector: 'mmx-appointment-estimate-card',
  templateUrl: './appointment-estimate-card.component.html',
  styleUrls: ['./appointment-estimate-card.component.scss'],
  standalone: false,
})
export class AppointmentEstimateCardComponent implements OnInit, OnDestroy {
  @Output() confirmedInvoice = new EventEmitter<InvoiceModel>()

  form = new UntypedFormGroup({})
  clinic: Clinic
  invoice: InvoiceModel
  patient: Patient
  appointment: AppointmentModel
  appointmentHasEpisode: boolean
  estimate: EstimateModel
  chargeGrouping: ChargeGrouping
  price: PriceModel
  products: ProductModel[] = []
  eligibility: Eligibility
  location: Facility | void
  filteredCodes: { [key: string]: Observable<any> } = {}
  filteredCodeModifiers: { [key: string]: Observable<any> } = {}
  procedureData: { [key: string]: Procedure } = {}
  fuseCodes: { [key: string]: Fuse<Procedure> } = {}
  fuseCodeModifiers: { [key: string]: Fuse<ProcedureCodeModifier> } = {}
  codeSuggestions: { query: string; suggestions: ProcedureData[] }[] = []
  estimateDetails: AppointmentEstimateSchema | (EstimateOutput & EstimateInputs)
  showUnits = false

  progress$ = this.progressService.progress$

  isSavingRate$ = this.progressService.inProgress('saving-rate')
  isSavingCode$ = this.progressService.inProgress('saving-code')
  isUpdatingEstimate$ = this.progressService.inProgress('saving-estimate')

  displayedCmdColumns = ['type', 'procedure', 'cpt', 'units']
  prices = []
  private onDestroy$ = new Subject<void>()

  constructor(
    public app: AppService,
    private router: Router,
    private data: AppointmentDataService,
    private patientDataService: PatientDataService,
    private restService: ApiService,
    private crudService: CrudService,
    private clinicService: ClinicService,
    private facilityService: FacilityService,
    private confirmService: ConfirmService,
    private translateService: TranslateService,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
    private progressService: ProgressService<
      | 'init'
      | 'saving-rate'
      | 'saving-code'
      | 'updating-appointment'
      | 'create-invoice'
      | 'load-estimate'
      | 'saving-estimate'
      | 'code-suggestions'
    >,
  ) {}

  get coinsurancePercent() {
    if (isNumber(this.estimateDetails.coinsurance)) {
      return Math.round(this.estimateDetails.coinsurance * 10000) / 100
    }
  }

  set coinsurancePercent(percentage) {
    if (isNumber(percentage)) {
      if (percentage > 100) {
        percentage = 100
      }

      this.estimateDetails.coinsurance = percentage / 100
    } else {
      this.estimateDetails.coinsurance = null
    }
  }

  get isPricerEnabled() {
    return this.clinic?.pricer.enable
  }

  /**
   * When enabled (per-clinic setting), the legacy estimate card and PDF surface the
   * billed/global rate alongside the allowed rate and patient responsibility.
   */
  get showGlobalRate(): boolean {
    return this.clinic?.settings?.showGlobalRateOnEstimate === true
  }

  // if we do not have a primary insurance, the estimate card cannot calculate properly…
  // or if the insurance plan is inactive, we cannot use the insurance we have to calculate properly…
  hasNoEligibility$: Observable<boolean> = combineLatest([
    this.patientDataService.insurances$,
    // this.clinic/this.appointment come from a separate, slower combineLatest in ngOnInit() -
    // reading them here would race insurances$ on reload; seed clinic$ so a loaded clinic isn't skipped.
    this.clinicService.clinic$.pipe(startWith(this.clinicService.clinic)),
    this.data.appointment$,
  ]).pipe(
    map(([insurances, clinic, appointment]) => {
      const accountId = appointment?.accountId
      const perVisitEnabled = isPerVisitInsuranceSelectionEnabled(clinic)

      // getAccountType() alone can't exclude a same-typed policy from a different account
      const activePrimaries = insurances.filter((insurance) => {
        return (
          isInsuranceRelevantToVisit(insurance, clinic, accountId) &&
          insurance.getAccountType(accountId, perVisitEnabled) === PatientInsuranceType.PRIMARY &&
          insurance.status === PatientInsuranceStatus.Active &&
          // @ts-ignore TODO: when we move appointment model to our shared lib, we can remove this
          insurance.eligibilityStatus ===
            Appointment.ELIGIBILITY_STATUS.ACTIVE
        )
      })

      return activePrimaries.length === 0
    }),
  )

  products$ = this.data.products$

  hasInsurances$ = this.patientDataService.insurances$.pipe(
    map((insurances) => insurances.length > 0),
  )

  hasNoProducts$ = this.products$.pipe(
    map((products) => !products || products.length === 0),
  )

  get productsMissingNegotiatedRate(): ProductModel[] {
    if (this.products && this.products.length) {
      return filter(this.products, (product) => {
        return !product.price
      })
    } else {
      return []
    }
  }

  get productsMissingServiceCode(): ProductModel[] {
    if (this.products && this.products.length > 0) {
      return filter(this.products, (product) => {
        return !product.serviceCodes || product.serviceCodes.length === 0
      })
    } else {
      return []
    }
  }

  ngOnInit() {
    this.estimateDetails = this.defaultEstimate()

    combineLatest([
      this.clinicService.clinic$.pipe(takeUntil(this.onDestroy$)),
      this.facilityService.current$.pipe(takeUntil(this.onDestroy$)),
      this.patientDataService.patient$.pipe(takeUntil(this.onDestroy$)),
      this.data.appointment$.pipe(takeUntil(this.onDestroy$)),
      this.patientDataService.insurances$.pipe(takeUntil(this.onDestroy$)),
    ])
      .pipe(
        takeUntil(this.onDestroy$),
        tap(([clinic, facility, patient, appointment]) => {
          this.clinic = clinic
          this.location = facility
          this.patient = patient
          this.appointment = appointment
          this.appointmentHasEpisode = !!this.appointment.episodeId
        }),
        debounceTime(1000),
        concatMap(() => this.calculate()),
      )
      .subscribe()

    this.permissionsService
      .hasPermission('appointment.estimate.charge.view')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isAllow) => {
        if (isAllow) {
          this.displayedCmdColumns = [
            'type',
            'procedure',
            'cpt',
            'units',
            'rate',
          ]
        }
      })
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onOutOutPocketNilChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.estimateDetails.outOfPocketMax = null
    } else {
      this.estimateDetails.outOfPocketMax = 0
    }

    this.saveEstimate()
  }

  onPopoverKeyup(event: KeyboardEvent, popup: any, closeOnEnter = true) {
    if ((closeOnEnter && event.key === 'Enter') || event.key === 'Escape') {
      event.stopPropagation()
      popup.close()
      ;(event.target as any).blur()
    }
  }

  calculate() {
    if (!this.isPricerEnabled) {
      return this.getDefaultAppointmentEstimate()
    }

    if (this.appointment.estimateId) {
      return this.getExistingEstimate()
    }

    return this.createNewEstimate()
  }

  getDefaultCurrency(currency?: string): string {
    try {
      return currency ?? this.clinic.settings.defaultCurrency ?? 'USD'
    } catch (ex) {
      return 'USD'
    }
  }

  saveCode(product: AppointmentEstimateProduct) {
    if (product.carrierLookupId) {
      this.progressService.start('saving-code')
      this.restService
        .patchProduct(product.id, { code: product.code })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.progressService.stop('saving-code')),
          tap(() => this.resetEstimate()),
        )
        .subscribe()
    }
  }

  saveRate(product: AppointmentEstimateProduct) {
    if (product.carrierLookupId) {
      this.progressService.start('saving-rate')

      const price = new PriceModel({
        productId: product.id,
        price: product.price,
        priceCurrency: product.priceCurrency || 'USD',
        requirements: {
          carrierLookups: [product.carrierLookupId],
        },
      })

      this.restService
        .createProductPrice(price)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.progressService.stop('saving-rate')),
          tap(() => this.resetEstimate()),
        )
        .subscribe()
    }
  }

  priceChanged(
    productSelected: AppointmentEstimateProduct,
    updatedValue: number,
  ) {
    // Update the product prices
    ;(this.estimateDetails as AppointmentEstimateSchema).products = (
      this.estimateDetails as AppointmentEstimateSchema
    ).products.map((product) =>
      Object.is(product, productSelected)
        ? { ...product, price: updatedValue, priceType: 'manual' }
        : product,
    )
    // Calculate the total using reduce and streamline the process
    this.estimateDetails.total = (
      this.estimateDetails as AppointmentEstimateSchema
    ).products.reduce((total, product) => total + (product.price || 0), 0)

    this.saveEstimate()
  }

  detectProcedureCodeChanged(product: AppointmentEstimateProduct) {
    const codeControl = this.form.get(`${product.id}-code`)
    const codeModifierControl = this.form.get(`${product.id}-codeModifier`)
    if (codeControl != null && codeModifierControl != null) {
      const code = codeControl.value
      const codeModifier = codeModifierControl.value
      this.procedureCodeChanged(product, code, codeModifier)
    }
  }

  procedureCodeChanged(
    product: AppointmentEstimateProduct,
    code?: string,
    codeModifier?: string,
  ) {
    if (
      typeof code === 'string' &&
      product.code !== code &&
      !(product.code == null && code === '')
    ) {
      product.code = code.length > 0 ? code : null
      product.codeType = 'manual'
      this.saveEstimate()
    }

    if (
      typeof codeModifier === 'string' &&
      product.codeModifier !== codeModifier &&
      !(product.codeModifier == null && codeModifier === '')
    ) {
      product.codeModifier = codeModifier.length > 0 ? codeModifier : null
      product.codeType = 'manual'
      this.saveEstimate()
    }
  }

  valueChanged() {
    this.saveEstimate()
  }

  closed() {
    // this.calculate()
  }

  confirmInvoice(chargeGrouping: ChargeGrouping) {
    const invoiceData = this.generateInvoice(chargeGrouping)
    let total = this.calculateTotal(chargeGrouping)

    this.invoice = new InvoiceModel(
      extend(
        {
          status:
            this.estimateDetails.patientsResponsibility === 0
              ? Invoice.STATUS.PAID
              : Invoice.STATUS.CONFIRMED,
          patientId: this.patient.id,
          collectionMethod: Invoice.COLLECTION_METHOD.UPCOMING,
          currency: this.getDefaultCurrency(),
          amountDue: 0,
          amountPaid: 0,
          amountRemaining: 0,
          lineItems: [],
          attemptCount: 0,
          subtotal: 0,
          tax: 0, // We need to calculate it
          taxAmounts: [],
          total: 0,
          estimate: this.estimateDetails,
        },
        invoiceData,
      ),
    )

    if (this.location && this.location.defaultTaxes) {
      this.invoice.taxes = this.location.defaultTaxes
      this.invoice.total = this.invoice.subtotal + this.invoice.tax
      this.invoice.amountDue = this.invoice.total
      this.invoice.amountRemaining = this.invoice.total
    }

    if (this.estimateDetails.total !== total) {
      this.confirmService
        .confirm(
          this.translateService.instant(
            'patient-details.estimate-card.confirm-dialog-text',
          ),
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((isConfirmed) => {
          if (isConfirmed) {
            this.postInvoiceUpdate()
          }
        })
    } else {
      this.postInvoiceUpdate()
    }
  }

  // create a bill with calculated estimate
  createInvoice() {
    const chargeGrouping = this.estimate.chargeGroupings[0]
    const invoiceData = this.generateInvoice(chargeGrouping)

    this.router.navigate(['patient', this.patient.id, 'invoices', 'new'], {
      state: invoiceData,
    })
  }

  resetEstimate() {
    this.confirmService
      .confirm({
        message:
          'Are you sure you want to reset the estimate? This will clear your changes.',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.saveEstimate(true)
        }
      })
  }

  hasSuggestion(product: AppointmentEstimateProduct, index: number) {
    const queryResult = this.codeSuggestions.find(
      (r) => r.query === product.name,
    )
    return queryResult?.suggestions[index] != null
  }

  getSuggestion(product: AppointmentEstimateProduct, index: number) {
    const queryResult = this.codeSuggestions.find(
      (r) => r.query === product.name,
    )
    if (queryResult) {
      return queryResult.suggestions[index]
    }
  }

  loadSuggestions() {
    const existingControls = Object.keys(this.form.controls)
    for (const existingControl of existingControls) {
      this.form.removeControl(existingControl)
    }

    const proceduresWithoutCode = this.products
      .filter((p) => p.type === Product.TYPE.MEDICAL_PROCEDURE)
      .filter((p) => p.code == null)
    // .filter((p) => this.estimateDetails.products.find((ep) => ep.id === p.id)?.code == null)

    for (const product of proceduresWithoutCode) {
      const appointmentProduct = (
        this.estimateDetails as AppointmentEstimateSchema
      ).products.find((ap) => ap.id === product.id)
      const codeControl = new UntypedFormControl(appointmentProduct?.code)
      const codeModifierControl = new UntypedFormControl(
        appointmentProduct?.code,
      ) // Change to codeModifier
      const codeFuse = new Fuse([], {
        keys: ['code'],
        shouldSort: true,
      })
      const codeModifierFuse = new Fuse([], {
        keys: ['code'],
        shouldSort: true,
      })

      // if (appointmentProduct) {
      //   control.valueChanges.pipe(
      //     takeUntil(this.onDestroy$),
      //     distinctUntilChanged(),
      //     debounceTime(1000),
      //     tap((value) => {
      //       this.procedureCodeChanged(appointmentProduct, value)
      //     }),
      //   ).subscribe()
      // }

      this.fuseCodes[product.id] = codeFuse
      this.filteredCodes[product.id] = codeControl.valueChanges.pipe(
        takeUntil(this.onDestroy$),
        startWith(''),
        distinctUntilChanged(),
        concatMap((value) => {
          const suggestions =
            this.codeSuggestions.find((s) => s.query === product.name)
              ?.suggestions ?? []
          if (isValidCPT(value)) {
            if (suggestions.find((p) => p.code === value)) {
              return of(codeFuse.search(value).map((r) => r.item))
            } else {
              return this.restService.getProcedureInfo(value, true).pipe(
                takeUntil(this.onDestroy$),
                tap((procedure) => {
                  this.procedureData[product.id] = procedure
                  const modifiers = procedure.supportedModifiers.map(
                    (m) => new ProcedureCodeModifier(m),
                  )
                  this.fuseCodeModifiers[product.id].setCollection(modifiers)
                }),
                catchError(() => {
                  if (value) {
                    return of(codeFuse.search(value).map((r) => r.item))
                  } else {
                    return of(suggestions.slice())
                  }
                }),
              )
            }
          } else if (value) {
            return of(codeFuse.search(value).map((r) => r.item))
          } else {
            return of(suggestions.slice())
          }
        }),
        map((value) => (isArray(value) ? value : [value])),
      )

      this.fuseCodeModifiers[product.id] = codeModifierFuse
      this.filteredCodeModifiers[product.id] =
        codeModifierControl.valueChanges.pipe(
          takeUntil(this.onDestroy$),
          startWith(''),
          distinctUntilChanged(),
          concatMap((value) => {
            if (value) {
              return codeModifierFuse.search(value ?? '').map((r) => r.item)
            } else if (this.procedureData[product.id]) {
              return this.procedureData[product.id].supportedModifiers ?? []
            } else {
              return []
            }
          }),
        )

      this.form.addControl(`${product.id}-code`, codeControl)
      this.form.addControl(`${product.id}-codeModifier`, codeModifierControl)
    }

    if (proceduresWithoutCode.length > 0) {
      this.progressService.start('code-suggestions')
      const procedureNames = proceduresWithoutCode.map((p) => p.name)
      this.restService
        .getProcedureCodeSuggestions(procedureNames)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.progressService.stop('code-suggestions')),
        )
        .subscribe({
          next: (results) => {
            this.codeSuggestions = results

            const controlNames = Object.keys(this.form.controls)
            for (const controlName of controlNames) {
              const control = this.form.controls[controlName]
              control.updateValueAndValidity({
                onlySelf: true,
                emitEvent: true,
              })
            }
          },
        })
    }
  }

  secondaryCoverageSummary() {
    const covers: string[] = []
    const notCovered: string[] = []

    if (this.estimateDetails.secondaryInsuranceCoversDeductible) {
      covers.push('deductible')
    } else {
      notCovered.push('deductible')
    }
    if (this.estimateDetails.secondaryInsuranceCoversCopayment) {
      covers.push('copayment')
    } else {
      notCovered.push('copayment')
    }
    if (this.estimateDetails.secondaryInsuranceCoversCoinsurance) {
      covers.push('coinsurance')
    } else {
      notCovered.push('coinsurance')
    }

    if (covers.length > 0 && notCovered.length > 0) {
      return `Covers ${covers.join(
        ', ',
      )}.<br><strong>Does not cover ${notCovered.join(', ')}.</strong>`
    } else if (covers.length === 3) {
      return `Covers deductible, copayment, and coinsurance.`
    } else if (covers.length === 2) {
      return `Covers ${covers[0]} and ${covers[1]}.`
    } else if (covers.length > 0) {
      return `Covers ${covers.join(', ')}.`
    } else {
      return `N/A`
    }
  }

  public openChargesDialog() {
    const data: ChargesDialogData = {
      estimate: this.estimate,
      facilityId: this.appointment.locationId,
      patientId: this.appointment.patientId,
      appointmentId: this.appointment.id,
    }
    this.dialog
      .open(ChargesDialogComponent, {
        autoFocus: false,
        panelClass: 'dialog-form',
        width: '1000px',
        minHeight: '800px',
        data,
      })
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          if (data.action === 'delete') {
            this.estimateDetails = this.defaultEstimate()
            this.appointment.estimateId = null
            this.estimate = null
          } else {
            this.appointment.estimateId = data.id
            this.estimate = data
            this.estimateDetails = extend({}, this.estimate.inputs, this.estimate.output)
            this.determineChargeGroup()
          }
        }
      })
  }

  private determineChargeGroup() {
    this.chargeGrouping = this.estimate.chargeGroupings.length > 1
      ? this.estimate.chargeGroupings.find(group => group.appointmentId === this.appointment.id)
      : this.estimate.chargeGroupings[0]
  }

  private updateEstimates(estimate: AppointmentEstimateSchema) {
    this.progressService.start('saving-estimate')
    this.estimate.inputs = extend(this.estimate.inputs, estimate)
    this.crudService
      .save(this.estimate)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop('saving-estimate')),
      )
      .subscribe((estimate) => {
        if (estimate) {
          this.estimate = estimate
          this.estimateDetails = extend({}, estimate.inputs, estimate.output)
          this.determineChargeGroup()
        }
      })
  }

  private updateAppointmentEstimateObject(estimate: AppointmentEstimateSchema) {
    this.progressService.start('updating-appointment')
    this.restService
      .updateAppointmentEstimate(this.appointment.id, estimate)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop('updating-appointment')),
      )
      .subscribe({
        next: (resp) => {
          if (resp.data) {
            this.products = resp.data.products
            this.estimateDetails = resp.data.estimate
          }
        },
        error: () => {
          // we failed to save the estimate, so reload the estimate from the server
          this.calculate().subscribe()
        },
      })
  }

  pbTrace(chargeGrouping: ChargeGrouping = this.chargeGrouping) {
    const pbResponseKey = this.findChargeGrouping(chargeGrouping, this.estimate.pbResponses)
    const pbRequestKey = this.findChargeGrouping(chargeGrouping, this.estimate.pbRequests)

    const response = pbResponseKey ? this.estimate.pbResponses.get(pbResponseKey) : undefined
    const request = pbRequestKey ? this.estimate.pbRequests?.get(pbRequestKey) : undefined

    const t = (key: string, params?: object) =>
      this.translateService.instant(`patient-details.estimate-card.advanced.${key}`, params)

    const message = buildPricerTraceMessage('pb', response, request, t)
    if (!message) {
      return
    }

    this.confirmService.confirm({
      message,
      html: true,
      hideNoButton: true,
      yesBtnLabel: 'common.close',
      width: '800px',
    })
  }

  hbTrace(chargeGrouping: ChargeGrouping = this.chargeGrouping) {
    const hbResponseKey = this.findChargeGrouping(chargeGrouping, this.estimate.hbResponses)
    const hbRequestKey = this.findChargeGrouping(chargeGrouping, this.estimate.hbRequests)

    const response = hbResponseKey ? this.estimate.hbResponses.get(hbResponseKey) : undefined
    const request = hbRequestKey ? this.estimate.hbRequests?.get(hbRequestKey) : undefined

    const t = (key: string, params?: object) =>
      this.translateService.instant(`patient-details.estimate-card.advanced.${key}`, params)

    const message = buildPricerTraceMessage('hb', response, request, t)
    if (!message) {
      return
    }

    this.confirmService.confirm({
      message,
      html: true,
      hideNoButton: true,
      yesBtnLabel: 'common.close',
      width: '800px',
    })
  }

  hasPBTrace(chargeGrouping: ChargeGrouping = this.chargeGrouping): boolean {
    return !!(this.findChargeGrouping(chargeGrouping, this.estimate?.pbResponses) ||
              this.findChargeGrouping(chargeGrouping, this.estimate?.pbRequests))
  }

  hasHBTrace(chargeGrouping: ChargeGrouping = this.chargeGrouping): boolean {
    return !!(this.findChargeGrouping(chargeGrouping, this.estimate?.hbResponses) ||
              this.findChargeGrouping(chargeGrouping, this.estimate?.hbRequests))
  }

  private findChargeGrouping<T>(
    grouping: ChargeGrouping,
    map?: Map<ChargeGrouping, T>,
  ): ChargeGrouping | undefined {
    if (!map) {
      return undefined
    }

    for (const key of map.keys()) {
      if (
        key.visitNumber === grouping.visitNumber &&
        key.appointmentId === grouping.appointmentId
      ) {
        return key
      }
    }

    return undefined
  }

  private postInvoiceUpdate() {
    this.progressService.start('create-invoice')
    this.restService
      .post<any>('/invoices', this.invoice.toJSON())
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop('create-invoice')),
      )
      .subscribe((resp) => {
        if (resp.data) {
          this.invoice = new InvoiceModel(resp.data)
          if (this.estimate) {
            this.estimate.status = Estimate.STATUS.CONFIRMED
            this.crudService.save(this.estimate).subscribe(() => {
              this.confirmedInvoice.emit()
            })
          } else {
            this.confirmedInvoice.emit()
          }
        }
      })
  }

  private saveEstimate(reset = false) {
    if (this.appointment == null) {
      return
    }

    const estimate: AppointmentEstimateSchema = {
      lineItems: [], // we do not save line items, the value is generated
      coinsurance: this.forceNumber(this.estimateDetails.coinsurance || 0),
      total: this.forceNumber(this.estimateDetails.total || 0),
      totalBillableToPatient:
        this.estimateDetails.total == null
          ? null
          : this.forceNumber(this.estimateDetails.total),
      deductible: this.forceNumber(this.estimateDetails.deductible || 0),
      copayment: this.forceNumber(this.estimateDetails.copayment || 0),
      outOfPocketMax:
        typeof this.estimateDetails.outOfPocketMax === 'number'
          ? this.forceNumber(this.estimateDetails.outOfPocketMax)
          : null,
      outOfPocketMaxApplied: this.estimateDetails.outOfPocketMaxApplied || 0,
      products: (this.estimateDetails as AppointmentEstimateSchema).products,
      insuranceType: this.estimateDetails.insuranceType || 'medical',
      hasSecondary: this.estimateDetails.hasSecondary,
      secondaryInsuranceCoversDeductible:
        this.estimateDetails.secondaryInsuranceCoversDeductible,
      secondaryInsuranceCoversCopayment:
        this.estimateDetails.secondaryInsuranceCoversCopayment,
      secondaryInsuranceCoversCoinsurance:
        this.estimateDetails.secondaryInsuranceCoversCoinsurance,
      estimateType: 'custom',
    }

    if (reset) {
      estimate.estimateType = 'reset'
    }

    if (this.estimate) {
      this.updateEstimates(estimate)
    } else {
      this.updateAppointmentEstimateObject(estimate)
    }
  }

  private defaultEstimate(): AppointmentEstimateSchema {
    return {
      deductible: 0,
      coinsurance: 0,
      copayment: 0,
      outOfPocketMax: null,
      products: [],
      lineItems: [],
      total: 0,
      totalBillableToPatient: null,
      estimateType: 'patientpal',
      insuranceType: 'medical',
      confidence: 0,
      coinsuranceApplied: 0,
      deductibleApplied: 0,
      copaymentApplied: 0,
      outOfPocketMaxApplied: 0,
      insuranceResponsibility: 0,
      patientsResponsibility: 0,
    }
  }

  private forceNumber(num: number) {
    if (typeof num !== 'number') {
      num = parseFloat(String(num).replace(/[^\d.]/g, ''))
    }
    if (typeof num === 'number') {
      return num
    }
  }

  private getDefaultAppointmentEstimate() {
    this.progressService.start('load-estimate')
    return this.restService.getAppointmentEstimate(this.appointment.id).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.progressService.stop('load-estimate')),
      tap((data) => this.handleAppointmentData(data)),
    )
  }

  private getExistingEstimate() {
    this.progressService.start('load-estimate')
    return this.crudService
      .read(EstimateModel, this.appointment.estimateId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop('load-estimate')),
        tap((estimate) => {
          if (estimate?.id) {
            this.estimate = estimate
            this.estimateDetails = extend({}, estimate.inputs, estimate.output)
            this.determineChargeGroup()
          }
        }),
      )
  }

  private createNewEstimate() {
    const cptCodes = this.flattenCPTCodes()
    const searchParams = {
      cptCodes,
      facilityIds: [this.appointment.locationId],
    }

    return this.restService
      .searchChargeDescriptionMaster(searchParams)
      .pipe(tap((charges) => this.processCharges(charges)))
  }

  private flattenCPTCodes() {
    return this.appointment.products.flatMap((product) => product.getCPTCodes())
  }

  private processCharges(charges: ChargeDescriptionMasterData[]) {
    const data = charges.map(({ codeModifiers, ...charge }) => ({
      ...charge,
      description: charge.chargeDescription,
      modifiers: seedChargeModifiers(charge.modifiers, codeModifiers),
      quantity: 1,
    }))
    const newEstimate = new EstimateModel({
      patientId: this.appointment.patientId,
      facilityId: this.appointment.locationId,
      appointmentIds: [this.appointment.id],
      patientType: Estimate.PATIENT_TYPE.OutPatient,
      charges: data,
    })

    this.saveNewEstimate(newEstimate)
  }

  private saveNewEstimate(estimate: EstimateModel) {
    this.progressService.start('saving-estimate')
    this.crudService
      .save(estimate)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.progressService.stop('saving-estimate')),
      )
      .subscribe((estimate) => {
        if (estimate) {
          this.estimate = estimate
          this.estimateDetails = extend({}, estimate.inputs, estimate.output)
          this.determineChargeGroup()
        }
      })
  }

  private handleAppointmentData(data: any) {
    if (data) {
      this.products = data.products.map((product) => new ProductModel(product))
      this.estimateDetails = data.estimate
      this.loadSuggestions()
    }
  }

  private calculateDueDate(): moment.Moment {
    let dueDate: moment.Moment = this.appointment.startAt
      ? this.appointment.startAt.clone().startOf('day')
      : moment.utc().add(30, 'days')
    if (dueDate.isBefore(moment.utc())) {
      dueDate = moment.utc().add(30, 'days')
    }
    return dueDate
  }

  private createLineItems(chargeGrouping: ChargeGrouping): InvoiceLineItem[] {
    let lineItems: InvoiceLineItem[] = this.isPricerEnabled
      ? this.estimate.lineItems
      : (this.estimateDetails as AppointmentEstimateSchema)?.lineItems ?? []

    const defaultCurrency = this.clinic.settings.defaultCurrency ?? 'USD'

    if (this.isPricerEnabled) {
      const addLineItem = (
        description: string,
        amount: number,
        expectedAmount: number,
      ) => {
        lineItems.push({
          description,
          amount,
          expectedAmount,
          currency: defaultCurrency,
        })
      }

      const hbResponse = this.estimate.hbResponses?.get(chargeGrouping)
      const pbResponse = this.estimate.pbResponses?.get(chargeGrouping)
      const hbTotal = hbResponse && !isPricerError(hbResponse) ? hbResponse.totalPrice : null
      const pbTotal = pbResponse && !isPricerError(pbResponse) ? pbResponse.price : null

      if (hbTotal != null && pbTotal != null) {
        lineItems = []
        addLineItem(
          'Outpatient Services',
          this.estimate.getTotalHBCharges(chargeGrouping) + this.estimate.getTotalPBCharges(chargeGrouping),
          hbTotal + pbTotal,
        )
      } else if (hbTotal != null) {
        lineItems = []
        addLineItem(
          this.estimate.patientType === Estimate.PATIENT_TYPE.OutPatient
            ? 'Outpatient Services'
            : 'Inpatient Services',
          this.estimate.getTotalHBCharges(chargeGrouping),
          hbTotal,
        )
      } else if (pbTotal != null && this.estimate.pbResponses?.size > 0) {
        addLineItem(
          'Outpatient Services',
          this.estimate.getTotalPBCharges(chargeGrouping),
          pbTotal,
        )
      }
    }

    return lineItems
  }

  private adjustLineItemsIfNeeded(
    lineItems: InvoiceLineItem[],
    total: number,
  ): void {
    if (this.estimateDetails.total !== total) {
      lineItems.push({
        description: 'Adjustment',
        amount: this.estimateDetails.total - total,
        currency: this.clinic.settings.defaultCurrency ?? 'USD',
      })
    }
  }

  private generateInvoice(chargeGrouping: ChargeGrouping): InvoiceData {
    const total = this.calculateTotal(chargeGrouping)
    const dueDate = this.calculateDueDate()
    const lineItems = this.createLineItems(chargeGrouping) ?? []

    this.adjustLineItemsIfNeeded(lineItems, total)

    return {
      patientId: this.patient.id,
      appointmentId: this.appointment.id,
      lineItems,
      subtotal: this.estimateDetails.patientsResponsibility,
      total: this.estimateDetails.patientsResponsibility,
      amountDue: this.estimateDetails.patientsResponsibility,
      amountRemaining: this.estimateDetails.patientsResponsibility,
      dueDate,
    }
  }

  private calculateTotal(chargeGrouping: ChargeGrouping): number {
    if (this.estimate?.id) {
      const hbEntry = this.estimate.hbResponses?.get(chargeGrouping)
      const pbEntry = this.estimate.pbResponses?.get(chargeGrouping)
      const hbTotal = hbEntry && !isPricerError(hbEntry) ? (hbEntry.totalPrice ?? 0) : 0
      const pbTotal = pbEntry && !isPricerError(pbEntry) ? (pbEntry.price ?? 0) : 0
      return round(hbTotal + pbTotal, 2)
    } else {
      return (
        (this.estimateDetails as AppointmentEstimateSchema).products?.reduce(
          (sum, product) => sum + (product.price ?? 0),
          0,
        ) ?? 0
      )
    }
  }
}
